import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Linkedin,
  Mail,
  ArrowRight,
  Database,
  Users2,
  TrendingUp
} from 'lucide-react';
import GlassCard from './ui/GlassCard';
import { Link } from 'react-router-dom';

const AboutView: React.FC = () => {
  return (
    <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 right-0 h-[600px] overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-emerald-400/10 dark:bg-emerald-500/5 blur-[100px] rounded-full animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute top-40 -left-20 w-[400px] h-[400px] bg-blue-400/10 dark:bg-blue-500/5 blur-[100px] rounded-full animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
      </div>

      <div className="mx-auto max-w-5xl px-6 pt-24 pb-32 space-y-24">

        {/* Hero Section */}
        <section className="text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex justify-center"
          >
            <div className="relative group">
              <div className="absolute inset-0 bg-emerald-500 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" />
              <img
                src="/logo.svg"
                alt="eduInsights Logo"
                className="h-24 w-24 relative z-10"
              />
            </div>
          </motion.div>

          <div className="space-y-4 max-w-3xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white"
            >
              Om <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500 dark:from-emerald-400 dark:to-teal-300">eduInsights</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed"
            >
              eduInsights är inte ett färdigt system och är heller inte tänkt att vara det – åtminstone inte än.
              Det är en plats där jag samlar tankar, analyser och perspektiv kring skoldata.
            </motion.p>
          </div>
        </section>

        {/* Mission Statement */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: <Database className="w-8 h-8 text-blue-500" />,
              title: "Öppen Data",
              desc: "Analyser av öppen skolstatistik från t.ex. Kolada eller SCB, presenterade på ett sätt som går att förstå utan att vara analytiker."
            },
            {
              icon: <TrendingUp className="w-8 h-8 text-emerald-500" />,
              title: "Kontext före Siffror",
              desc: "Siffror behöver alltid sättas i sitt sammanhang. Fokus ligger på helhet, jämförelser över tid och rimliga tolkningar."
            },
            {
              icon: <Users2 className="w-8 h-8 text-purple-500" />,
              title: "Samtal, inte Facit",
              desc: "Inget färdigt facit – istället underlag för samtal och flera perspektiv som ger utrymme att fundera vidare."
            }
          ].map((item, i) => (
            <GlassCard key={i} className="p-8 text-center" hoverEffect>
              <div className="mx-auto mb-6 p-3 rounded-2xl bg-white dark:bg-slate-800 w-fit shadow-sm border border-slate-100 dark:border-slate-700">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{item.title}</h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{item.desc}</p>
            </GlassCard>
          ))}
        </div>

        {/* About Urban Section */}
        <section className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 md:p-16">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" />

          <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                Urban Edlund
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                Jag heter Urban Edlund och har arbetat med data, analys och beslutsstöd i drygt 20 år.
              </p>
              <p className="text-slate-300">
                Det jag tar med mig hit är framför allt:
              </p>
              <ul className="space-y-2 text-slate-300">
                <li>• erfarenheten av hur data faktiskt används</li>
                <li>• förståelsen för att siffror alltid behöver sättas i sitt sammanhang</li>
                <li>• och respekt för att komplexa verksamheter sällan låter sig reduceras till enkla nyckeltal</li>
              </ul>

              <div className="flex gap-4 pt-4">
                <a
                  href="https://www.linkedin.com/in/urban-edlund-data-specialist/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/10"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="mailto:urban@edlund.xyz"
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/10"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10"
              >
                <img
                  src="/urban.jpg"
                  alt="Urban Edlund"
                  className="w-full h-auto object-cover grayscale opacity-90 hover:grayscale-0 transition-all duration-700"
                />
              </motion.div>
              {/* Decorative rings */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-500/20 blur-2xl rounded-full" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/20 blur-2xl rounded-full" />
            </div>
          </div>
        </section>

        {/* Privacy Section */}
        <section className="space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Integritet, som det ska vara</h2>
            <p className="text-slate-600 dark:text-slate-400">Eftersom all data som används redan är offentlig och inget sparas om dig eller dina val behöver du inte oroa dig för cookies, spårning eller dolda villkor.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 h-fit">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 dark:text-white mb-1">Det ska vara enkelt att vara här</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">All bearbetning sker lokalt på din enhet. Inga personuppgifter sparas.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-12">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-10">
            Om du är nyfiken
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto">
            Vill du veta mer, diskutera data eller bara säga hej? Du når mig enklast via LinkedIn eller e-post.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/kommun"
              className="px-8 py-4 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20"
            >
              Utforska analyserna
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:urban@kuub.app"
              className="px-8 py-4 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold hover:opacity-90 transition-all"
            >
              Kontakta mig
            </a>
          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutView;
